import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import Clock from '../Clock';
import { useUser } from '../../contexts/UserContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import { useDashboardView, DASHBOARD_VIEWS } from '../../contexts/DashboardViewContext';
import { readShellEnabled } from '../../features/shell/shellFlag';
import LayoutToggle from '../../features/shell/LayoutToggle';
import GlassPaper from '../GlassPaper';
import './Navbar.css'; // Import the CSS file for additional styles
import ContraLogo from '../../logo.svg';

const PRIMARY_LINKS = [
    { value: 'home', label: 'Home', path: '/' },
    { value: 'access', label: 'Access System', path: '/access' },
];

const Navbar = () => {
    const { user, logout } = useUser(); // Use the useUser hook to access the user context
    const { mode, toggleTheme } = useThemeMode();
    const { view, setView, triggerRefresh } = useDashboardView();
    const location = useLocation();
    const navigate = useNavigate();

    const shellEnabled = readShellEnabled();
    const isDashboard = location.pathname.startsWith('/dashboard');

    if (user && shellEnabled && isDashboard) {
        return null;
    }

    const navbarClass = `navbar ${mode === 'dark' ? 'navbar-dark' : 'navbar-light'} custom-navbar`;
    const showPrimaryLinks = !user;
    const primaryValue = showPrimaryLinks
        ? PRIMARY_LINKS.find((link) => link.path === location.pathname)?.value || null
        : null;
    const themeLogoMap = {
        light: ContraLogo,
        dark: ContraLogo,
    };
    const resolvedLogoSrc = themeLogoMap[mode] || ContraLogo;

    const handleSelection = (_event, nextView) => {
        if (!user || !nextView) {
            return;
        }
        if (nextView !== view) {
            setView(nextView);
        }
        triggerRefresh();
        if (!location.pathname.startsWith('/dashboard')) {
            navigate('/dashboard');
        }
    };

    const handlePrimarySelection = (_event, nextValue) => {
        if (!nextValue) {
            return;
        }
        const target = PRIMARY_LINKS.find((link) => link.value === nextValue);
        if (!target) {
            return;
        }
        setView(DASHBOARD_VIEWS[0].value);
        triggerRefresh();
        navigate(target.path);
    };

    const handleLogout = () => {
        logout();
        setView(DASHBOARD_VIEWS[0].value);
        triggerRefresh();
        navigate('/access');
    };

    return (
        <GlassPaper component="nav" className={`${navbarClass} glass-navbar`} elevation={0} square={false}>
            <div className="container-fluid navbar-main">
                <div className="navbar-left">
                    <a className="navbar-brand" href="/">
                        <img src={resolvedLogoSrc} alt="Contra logo" className="navbar-logo" />
                    </a>
                    {showPrimaryLinks && (
                        <ToggleButtonGroup
                            size="small"
                            color="primary"
                            value={primaryValue}
                            exclusive
                            onChange={handlePrimarySelection}
                            className="navbar-primary-links"
                        >
                            {PRIMARY_LINKS.map((link) => (
                                <ToggleButton
                                    key={link.value}
                                    value={link.value}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.2, py: 0.5 }}
                                >
                                    {link.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )}
                    {user && (
                        <>
                            <ToggleButtonGroup
                                size="small"
                                color="primary"
                                exclusive
                                value={view}
                                onChange={handleSelection}
                                className="navbar-views"
                            >
                                {DASHBOARD_VIEWS.map((option) => (
                                    <ToggleButton
                                        key={option.value}
                                        value={option.value}
                                        sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.2, py: 0.5 }}
                                    >
                                        {option.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </>
                    )}
                </div>
                <div className="navbar-right">
                    <span className="nav-link clock-link">
                        <Clock />
                    </span>
                    {user && (
                        <>
                            <span className="nav-link">Hello, {user.full_name}!</span>
                            {isDashboard && (
                                <LayoutToggle sx={{ color: 'inherit', mr: 0.5 }} />
                            )}
                            <button className="btn btn-outline-danger btn-sm" onClick={handleLogout}>
                                Logout
                            </button>
                        </>
                    )}
                    <Tooltip title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} theme`} placement="bottom">
                        <IconButton
                            size="small"
                            color="inherit"
                            onClick={toggleTheme}
                            className="navbar-theme-toggle"
                            aria-label="Toggle theme"
                        >
                            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
        </GlassPaper>
    );
};

export default Navbar;
